import React, {Component} from 'react';
import {Switch, Route, BrowserRouter} from 'react-router-dom';
import './App.css';

import {emphasize} from '@material-ui/core/styles/colorManipulator';
import {MuiThemeProvider, createMuiTheme} from '@material-ui/core/styles';
import Snackbar from '@material-ui/core/Snackbar';
import Button from '@material-ui/core/Button';

import queryString from 'query-string';

import {helper, redirect, url} from './components/common/common';
import DialogError from './components/common/DialogError';
import Home from './components/Home';
import Access from './components/Access';
import OrganizationSelect from './components/OrganizationSelect';
import OrganizationJoin from './components/OrganizationJoin';
import ContactUs from './components/ContactUs';
import Main from './components/Main';

const theme = {
    palette: {
        type: 'light',
        common: {
            black: '#000000',
            white: '#ffffff',

            transparent: 'rgba(0, 0, 0, 0)',
            red: {
                500: 'rgba(244, 67, 54, 1)',
            }
        },
        primary: {
            main: 'rgba(18, 26, 28, 1)',
            light: 'rgba(56, 64, 67, 1)',
            dark: 'rgba(0, 0, 0, 1)',
            contrastText: 'rgba(255, 255, 255, 1)',

            displayText: 'rgba(255, 255, 255, 0.87)',
            selectedDisplayText: 'rgba(206, 142, 64, 0.87)',
            disabledDisplayText: 'rgba(255, 255, 255, 0.35)',

            mainBorder: emphasize('rgba(18, 26, 28, 1)', 0.08),
            lightBorder: emphasize('rgba(56, 64, 67, 1)', 0.08),
            darkBorder: emphasize('rgba(0, 0, 0, 1)', 0.08),

            hoverOverlay: 'rgba(206, 142, 64, 0.17)'
        },
        secondary: {
            main: 'rgba(206, 142, 64, 1)',
            light: 'rgba(255, 190, 110, 1)',
            dark: 'rgba(153, 97, 16, 1)',
            contrastText: 'rgba(255, 255, 255, 1)',
        },
    },
    iconStyle: {
        size: {
            12: { width: '12px', height: '12px' },
            14: { width: '14px', height: '14px' },
            16: { width: '16px', height: '16px' },
            18: { width: '18px', height: '18px' },
            24: { width: '24px', height: '24px' },
            28: { width: '28px', height: '28px' },
            30: { width: '30px', height: '30px' },
            36: { width: '36px', height: '36px' },
            48: { width: '48px', height: '48px' },
            64: { width: '64px', height: '64px' },
            80: { width: '80px', height: '80px' },
            96: { width: '96px', height: '96px' },
        },
        dark: { color: 'rgba(18, 26, 28, 0.54)' },
        darkInactive: { color: 'rgba(18, 26, 28, 0.26)' },
        light: { color: 'rgba(245, 245, 245, 0.87)' },
        lightInactive: { color: 'rgba(245, 245, 245, 0.3)' },
        selected: { color: 'rgba(206, 142, 64, 0.87)' },
    },
    typography: {
        useNextVariants: true,
    },
    props: {
        MuiButtonBase: {
            disableRipple: true,
        }
    }
}

class App extends Component {
    constructor(props) {
        super(props);

        document.title = 'Alpha2';

        this.state = {
            errorPage: false,
            errorTitle: 'Error',
            errorValue: 'generic',
            errorMessage: '',
            hasNewVersion: false,
        };

        const prevCode = localStorage.getItem('accessCode');

        if (prevCode) {
            this._accessCode = prevCode;
        }

        this._appTheme = createMuiTheme(theme);

        window.addEventListener('error', (e) => {
            this._setError('Error', 'generic', 'An error has occured, please refresh to try again');
        });

        const params = helper.sanitizeDict(
            queryString.parse(window.location.search), 'user');

        this._user = params['user'];

        window.serviceWorkerCallback = this._serviceWorkerCallback;
    }

    componentDidCatch(error, info) {
        this.setState({errorPage: true})
    }

    render = () => {
        if (this.state.errorPage) {
            return (
                <div style={{textAlign: 'center'}}>
                    <h3>Oops. Something seems to have went wrong.</h3>
                    <h4>Please refresh to try again or contact support to resolve the issue.</h4>
                </div>
            );
        }

        return (
            <div className='App'>
                <MuiThemeProvider theme={this._appTheme}>
                    <div>
                        {this._renderRoute()}
                        {this._renderNewVersion()}
                        <DialogError
                            title={this.state.errorTitle}
                            value={this.state.errorValue}
                            message={this.state.errorMessage}
                            onOk={this._handleError}
                        />
                    </div>
                </MuiThemeProvider>
            </div>
        );
    }

    _renderRoute = () => {
        return (
            <BrowserRouter>
                <Switch>
                    <Route
                        exact path='/:base*/home'
                        render={this._renderHome}
                    />
                    <Route
                        exact path='/:base*/access'
                        render={this._renderAccess}
                    />
                    <Route
                        exact path='/:base*/organization-select'
                        render={this._renderOrganizationSelect}
                    />
                    <Route
                        exact path='/:base*/organization-select-silent'
                        render={this._renderOrganizationSelectSilent}
                    />
                    <Route
                        exact path='/:base*/organization-join'
                        render={this._renderOrganizationJoin}
                    />
                    <Route
                        exact path='/:base*/contact-us'
                        render={this._renderContactUs}
                    />
                    <Route
                        path='/:base*/main/'
                        render={this._renderMain}
                    />
                    <Route
                        exact path='/'
                        render={this._renderLandingPage}
                    />
                </Switch>
            </BrowserRouter>
        );
    }

    _renderHome = (props) => {
        return (
            <Home
                {...props}
                setError={this._setError}
                getAccessCode={this._getAccessCode}
            />
        );
    }

    _renderAccess = (props) => {
        return (
            <Access
                {...props}
                setError={this._setError}
                setAccessCode={this._setAccessCode}
            />
        );
    }

    _renderOrganizationSelect = (props) => {
        if (this._needResetToHomePage()) {
            return null;
        }

        return (
            <OrganizationSelect
                {...props}
                setError={this._setError}
                getAccessCode={this._getAccessCode}
                setAccessCode={this._setAccessCode}
            />
        );
    }

    _renderOrganizationSelectSilent = (props) => {
        if (this._needResetToHomePage()) {
            return null;
        }

        return (
            <OrganizationSelect
                {...props}
                silent={true}
                setError={this._setError}
                getAccessCode={this._getAccessCode}
                setAccessCode={this._setAccessCode}
            />
        );
    }

    _renderOrganizationJoin = (props) => {
        if (this._needRedirectJump(true)) {
            return null;
        }

        return (
            <OrganizationJoin
                {...props}
                setError={this._setError}
                getAccessCode={this._getAccessCode}
                setAccessCode={this._setAccessCode}
            />
        );
    }

    _renderContactUs = (props) => {
        return (
            <ContactUs
                {...props}
                setError={this._setError}
            />
        );
    }

    _renderMain = (props) => {
        if (this._needRedirectJump(false)) {
            return null;
        }

        return (
            <Main
                {...props}
                setError={this._setError}
                getAccessCode={this._getAccessCode}
                setAccessCode={this._setAccessCode}
            />
        );
    }

    _renderLandingPage = (props) => {
        redirect(window.location.origin + '/home' + window.location.search);
        return null;
    }

    _renderNewVersion = () => {
        return (
            <Snackbar
                style={{marginBottom: 16}}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                open={this.state.hasNewVersion}
                message={<span>A new version is available</span>}
                action={<Button color="secondary" size="small" onClick={this._handleRefresh}>Refresh</Button>}
            />
        );
    }

    _needResetToHomePage = () => {
        if (!this._accessCode) {
            redirect(url.login({}, this._user));
            return true;
        }

        return false;
    }

    _needRedirectJump = (preSelect) => {
        if (!this._accessCode) {
            if (preSelect) {
                redirect(url.login({
                    preSelectRouteTo: window.location.pathname,
                    search: window.location.search,
                }, this._user));
            } else {
                redirect(url.login({
                    routeTo: window.location.pathname,
                    search: window.location.search,
                }, this._user));
            }

            return true;
        }

        return false;
    }

    _getAccessCode = () => {
        return this._accessCode;
    }

    _setAccessCode = (code) => {
        this._accessCode = code;

        localStorage.setItem('accessCode', this._accessCode);
    }

    _handleRefresh = (value) => {
        this.setState({
            hasNewVersion: false,
        });

        window.location.reload();
    }

    _handleError = (value) => {
        if (this._errorCallback) {
            this._errorCallback(value)
        }

        this.setState({
            errorTitle: '',
            errorValue: 'generic',
            errorMessage: ''
        });
    }

    _setError = (title, value, message, callback) => {
        this._errorCallback = callback;

        this.setState({
            errorTitle: title,
            errorValue: value,
            errorMessage: message,
        });
    }

    _serviceWorkerCallback = (state) => {
        if (state === 'new_version') {
            this.setState({
                hasNewVersion: true
            });
        }
    }
}

export default App;
